import { isBefore } from 'date-fns';
import React, { useMemo } from 'react';
import * as Yup from 'yup';

import { PortfolioFeeRule } from '@ndvr/billing-api';

import { parseLocalDate, todayDate, tomorrowDate, useRequiredParams } from 'utils';
import { FormNumberInput, FormSection, requiredStringValidator } from 'widgets';
import { FormDateInput } from 'widgets/form/input/FormDateInput';

import { usePortfolioFeeRuleTimeline, useSavePortfolioFeeRule } from '../billing-queries';
import { getDisabledDates, getFirstAvailableDate, isDateDisabled } from '../billing-utils';
import { PortfolioSelect } from '../widgets/PortfolioSelect';
import { BillingDrawer } from './BillingDrawer';

interface Props {
    portfolioFeeRule?: PortfolioFeeRule;
    onClose: () => void;
}

export const PricingOverrideDrawer = ({ portfolioFeeRule, onClose }: Props) => {
    const { globalAccountGUID } = useRequiredParams(['globalAccountGUID']);
    const { mutate: savePortfolioFeeRule, isPending } = useSavePortfolioFeeRule(globalAccountGUID, !!portfolioFeeRule);

    const { data: timeline } = usePortfolioFeeRuleTimeline(globalAccountGUID);
    const disabledDates = getDisabledDates(
        timeline?.upcomingRules.filter((rule) => rule.portfolioFeeRuleGUID !== portfolioFeeRule?.portfolioFeeRuleGUID),
    );
    const firstAvailableDate = getFirstAvailableDate(disabledDates);

    const defaultValues = useMemo(() => {
        return portfolioFeeRule
            ? { ...portfolioFeeRule }
            : ({
                  globalAccountGUID,
                  annualBps: '',
                  startDate: firstAvailableDate,
              } as PortfolioFeeRule);
    }, [firstAvailableDate, globalAccountGUID, portfolioFeeRule]);

    const formSchema = Yup.object({
        portfolioGUID: requiredStringValidator.label('Account'),
        annualBps: requiredStringValidator
            .label('AUM charge')
            .test('max-aum-charge', 'AUM charge overrides cannot exceed 45bps', (value) => Number(value) <= 45),
        startDate: requiredStringValidator.test(
            'start-date-available',
            'Date already within an override interval',
            (startDate) => !isDateDisabled(parseLocalDate(startDate), disabledDates),
        ),
        endDate: Yup.string()
            .test(
                'end-date-available',
                'Date already within an override interval',
                (endDate) => !endDate || !isDateDisabled(parseLocalDate(endDate), disabledDates),
            )
            .test(
                'date-overlap',
                'Date range would overlap with existing one',
                (endDate, ctx) =>
                    !!endDate ||
                    disabledDates.every((disabledDate) => isBefore(disabledDate, parseLocalDate(ctx.parent.startDate))),
            ),
    });

    const editingActiveRule = useMemo(() => {
        if (!portfolioFeeRule) {
            return false;
        }
        return parseLocalDate(portfolioFeeRule.startDate) <= todayDate();
    }, [portfolioFeeRule]);

    return (
        <BillingDrawer
            title={portfolioFeeRule ? 'Update Pricing Override' : 'Create New Pricing Override'}
            editing={!!portfolioFeeRule}
            formSchema={formSchema}
            defaultValues={defaultValues}
            onSubmit={savePortfolioFeeRule}
            pending={isPending}
            onClose={onClose}
        >
            <FormSection>
                <PortfolioSelect
                    name="portfolioGUID"
                    globalAccountGUID={globalAccountGUID}
                    disabled={!!portfolioFeeRule}
                />
            </FormSection>
            <FormSection hasDivider>
                <FormNumberInput
                    name="annualBps"
                    label="Annual AUM Charge"
                    placeholder="enter annual bps"
                    decimalScale={2}
                    allowNegative={false}
                    disabled={editingActiveRule}
                    vertical
                />
            </FormSection>
            <FormSection hasDivider>
                <FormDateInput
                    name="startDate"
                    label="Start Date"
                    minDate={tomorrowDate()}
                    disabled={editingActiveRule}
                    disabledDates={disabledDates}
                />
                <FormDateInput
                    name="endDate"
                    label="End Date"
                    minDate={editingActiveRule ? todayDate() : tomorrowDate()}
                    disabledDates={disabledDates}
                />
            </FormSection>
        </BillingDrawer>
    );
};
